import type { AxiosRequestConfig } from 'axios';
import { Enterprise, Pillar2Auth, Report, ReportPillar2Settings, TestPillar2Settings } from '../../model';
import type { Pillar2FilingRequest } from '../../model/pillar2FilingRequest';
import type { Pillar2FilingResponse } from '../../model/pillar2FilingResponse';
import type { FetchError } from '../../utils/UseCustomInstance';
import { getJurisdictionBasePath, resolveJurisdiction } from './pillar2Jurisdictions';

export interface Pillar2FilingMeta {
    jurisdiction?: string | null;
    obligationType?: string | null;
    isNilReturn?: boolean;
}

/** Same shape as `useCustomInstance()` (untyped response; cast at call sites). */
export type Pillar2BffRequest = (config: AxiosRequestConfig) => Promise<unknown>;

/** Hook-sourced fields for submit (enterprise + tax return). */
export interface Pillar2FilingContext {
    enterpriseId: string;
    enterpriseName?: string | null;
    returnId: string;
    returnName?: string | null;
    entityCode?: string | null;
    caseCode?: string | null;
    periodCode?: string | null;
    returnKey: number;
    columnIndex: number;
}

export interface Pillar2FilingSubmitResult {
    response: Pillar2FilingResponse;
    httpStatus: number;
}

//** Validate error response from API to avoid parsing errors */
function isPillar2FilingResponse(data: unknown): data is Pillar2FilingResponse {
    return typeof data === 'object' && data !== null && 'operations' in data && Array.isArray((data as Pillar2FilingResponse).operations);
}

export function buildPillar2FilingContext(
    enterprise: Enterprise | null | undefined,
    report: Report | null | undefined
): Pillar2FilingContext | null {
    const enterpriseId = enterprise?.enterpriseId?.trim();
    const returnId = report?.taxReturnItemId?.trim();
    if (!enterpriseId || !returnId) {
        return null;
    }
    const periodTrimmed = report?.period?.trim();
    const periodCode = periodTrimmed || (report?.year != null && report.year !== undefined ? String(report.year) : null) || null;
    return {
        enterpriseId,
        enterpriseName: enterprise?.enterpriseName ?? null,
        returnId,
        returnName: report?.name ?? null,
        entityCode: report?.entityCode ?? null,
        caseCode: report?.case ?? null,
        periodCode,
        returnKey: report?.id ?? 0,
        columnIndex: 0,
    };
}

export async function exchangeUkAuthCode(bff: Pillar2BffRequest, code: string, redirectUri: string): Promise<void> {
    const basePath = getJurisdictionBasePath('GB');
    await bff({
        url: `${basePath}/auth/exchange`,
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        data: { code, redirectUri },
    });
}

export async function getPillar2Config(bff: Pillar2BffRequest, jurisdictionInput?: string | null): Promise<TestPillar2Settings> {
    const jurisdiction = resolveJurisdiction(jurisdictionInput);
    const basePath = getJurisdictionBasePath(jurisdiction);
    return (await bff({ url: `${basePath}/config`, method: 'get' })) as TestPillar2Settings;
}

export async function getPillar2Id(bff: Pillar2BffRequest, jurisdictionInput?: string | null): Promise<string> {
    const config = await getPillar2Config(bff, jurisdictionInput);
    const id = config.pillar2Id?.trim();
    if (!id) {
        throw new Error(`${jurisdictionInput || 'Pillar2'} config did not return pillar2Id.`);
    }
    return id;
}

export interface SamplesAccountingPeriod {
    fromDate: string;
    toDate: string;
}

export interface Pillar2EfileAccountingPeriod {
    dateFrom?: string;
    dateTo?: string;
}

function toMetadataDateString(value: unknown): string | undefined {
    if (typeof value !== 'string') {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
}

/** BeginDate/EndDate from report pillar2Settings.efileReturnMetadata (Report API). */
export function resolveEfileAccountingPeriod(pillar2Settings?: ReportPillar2Settings | null): Pillar2EfileAccountingPeriod {
    const metadata = pillar2Settings?.efileReturnMetadata;
    if (!metadata) {
        return {};
    }

    return {
        dateFrom: toMetadataDateString(metadata.BeginDate?.value),
        dateTo: toMetadataDateString(metadata.EndDate?.value),
    };
}

/**
 * Uses accounting period dates from sample payloads (same period as sample submission).
 */
export async function getSamplesAccountingPeriod(
    bff: Pillar2BffRequest,
    jurisdictionInput?: string | null
): Promise<SamplesAccountingPeriod> {
    const jurisdiction = resolveJurisdiction(jurisdictionInput);
    const basePath = getJurisdictionBasePath(jurisdiction);
    const samples = (await bff({ url: `${basePath}/samples`, method: 'get' })) as any;
    const fromDate =
        samples.submitLiability?.accountingPeriodFrom ??
        samples.submitNilReturn?.accountingPeriodFrom ??
        samples.amendLiability?.accountingPeriodFrom ??
        samples.amendNilReturn?.accountingPeriodFrom ??
        samples.btn?.accountingPeriodFrom ??
        samples.overseas?.accountingPeriodFrom ??
        samples.overseasNil?.accountingPeriodFrom;
    const toDate =
        samples.submitLiability?.accountingPeriodTo ??
        samples.submitNilReturn?.accountingPeriodTo ??
        samples.amendLiability?.accountingPeriodTo ??
        samples.amendNilReturn?.accountingPeriodTo ??
        samples.btn?.accountingPeriodTo ??
        samples.overseas?.accountingPeriodTo ??
        samples.overseasNil?.accountingPeriodTo;
    if (!fromDate || !toDate) {
        throw new Error(`${jurisdictionInput || 'Pillar2'} samples did not include accountingPeriodFrom/To.`);
    }
    return { fromDate, toDate };
}

export async function executePillar2Filing(
    bff: Pillar2BffRequest,
    filingMeta: Pillar2FilingMeta | null | undefined,
    context: Pillar2FilingContext,
    persistFilingPayloadToCloud: boolean,
    auth: Pillar2Auth,
    pillar2Settings: ReportPillar2Settings,
    efilePayloadOverride?: string | null
): Promise<Pillar2FilingResponse> {
    const jurisdiction = resolveJurisdiction(filingMeta?.jurisdiction);
    const basePath = getJurisdictionBasePath(jurisdiction);

    const { dateFrom, dateTo } = resolveEfileAccountingPeriod(pillar2Settings);

    const data: Pillar2FilingRequest = {
        jurisdiction: jurisdiction,
        filedJurisdiction: jurisdiction,
        obligationType: filingMeta?.obligationType ?? '',
        isNilReturn: filingMeta?.isNilReturn ?? false,
        enterpriseId: context.enterpriseId,
        enterpriseName: context.enterpriseName,
        returnId: context.returnId,
        returnName: context.returnName,
        entityCode: context.entityCode,
        caseCode: context.caseCode,
        periodCode: context.periodCode,
        persistFilingPayloadToCloud: persistFilingPayloadToCloud,
        auth: auth,
        settings: pillar2Settings,
        columnIndex: context.columnIndex,
        returnKey: context.returnKey,
        dateFrom,
        dateTo,
        // Debug-only: when set, the backend submits this content instead of fetching from the stack.
        ...(efilePayloadOverride ? { efilePayloadOverride } : {}),
    };

    try {
        const response = (await bff({
            url: `${basePath}/submit`,
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            data,
        })) as Pillar2FilingResponse;

        return response;
    } catch (err) {
        const fetchErr = err as FetchError;
        const status = fetchErr.response?.status;
        const body = fetchErr.response?.data;
        if (status === 500 && isPillar2FilingResponse(body)) {
            return body;
        }
        throw err;
    }
}

/** @deprecated Use getPillar2Config */
export const getUkPillar2Config = getPillar2Config;
/** @deprecated Use getPillar2Id */
export const getUkPillar2Id = getPillar2Id;
/** @deprecated Use getSamplesAccountingPeriod */
export const getUkSamplesAccountingPeriod = getSamplesAccountingPeriod;
